import ResourceManager from "../../components/ResourceManager/ResourceManager"
import { CalendarEventViewModel } from "../api/calendar-event"
import { useCalendarEventMutations } from "../hooks/use-calendar-event-mutations"
import { useI18n } from "../../hooks/use-i18n"
import { useOptions } from "../../options/hooks/use-options"
import { StringMap } from "../../types/common"

const EventParticipantsWidget = ({ calendarEvent }: { calendarEvent: CalendarEventViewModel }) => {
  const { updateParticipants } = useCalendarEventMutations()
  const translations = useTranslations(defaultTranslations)
  const { membershipOptions } = useOptions(calendarEvent.organisation?.id || "")

  const onAddParticipants = (ids: string[]) => {
    const participants = [
      ...calendarEvent.participants,
      ...membershipOptions.filter((p) => ids.includes(p.id)).map((p) => ({ id: p.id, name: p.name })),
    ]

    updateParticipants(calendarEvent.id, participants)
  }

  const onRemoveParticipant = (participantId: string) => {
    const participants = calendarEvent.participants.filter((p) => p.id !== participantId)

    updateParticipants(calendarEvent.id, participants)
  }

  const sortedMembershipOptions = membershipOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  const sortedParticipants = calendarEvent.participants
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedMembershipOptions}
      resources={sortedParticipants}
      canAddResource={calendarEvent.canUpdate}
      canRemoveResource={calendarEvent.canUpdate}
      onAddResources={onAddParticipants}
      onRemoveResource={onRemoveParticipant}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (
  defaults: EventParticipantsWidgetTranslations = defaultTranslations
): EventParticipantsWidgetTranslations => {
  const { translations: t } = useI18n("calendarEvent")
  const translations = (t?.participantsWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
    updateErrorMessage = defaults.updateErrorMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
    updateErrorMessage,
  }
}

const defaultTranslations = {
  title: "Participants",
  emptyOptionsMessage: "No members in this organisation",
  emptyResourcesMessage: "This event does not have participants",
  updateErrorMessage: "Failed to update participants",
}

type EventParticipantsWidgetTranslations = typeof defaultTranslations

export default EventParticipantsWidget
